// UC20-11 — add or correct a medicine and its codes. Archetype D.
// per docs/shape-uc-20-medicine-nomenclator.md §5
// The codes section writes: the previous form parsed a textarea, sent it, and
// got `codes: []` back from a write that had silently discarded it.
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudSwitch,
  MudIcon,
  MudTextInput,
  MudInfoBox,
  MudSelect,
  MudTooltip,
} from '@egov-moldova/mud-react';
import { isoToDateInput, onDateCommit, useToast, DraftDateInput } from '@simsm/ui-shared';
import {
  type ApiRefusal,
  apiRefusal,
  isNormalizedApiError,
  isRequestUnresolved,
  loadFailureKind,
} from '../../app/api-client';
import { FormField, FormPage, FormRow, FormSection } from '../../components/FormPage/FormPage';
import {
  MEDICINE_CODE_KINDS,
  MEDICINES_PAGE_SIZE,
  createMedicine,
  fetchMedicineCode,
  fetchMedicineCodePage,
  medicineLabel,
  setMedicineCodeActive,
  updateMedicine,
  upsertMedicineCode,
  type MedicineCodeKind,
  type MedicineCodeRow,
} from './medicines-api';
import {
  codePatch,
  createPayload,
  missingFields,
  positionIdentityPatch,
  requiredFields,
  type MedicineFormState as FormState,
  type RequiredField,
} from './medicine-form-payload';

/**
 * One registration, and the position it belongs to.
 *
 * The register lists medicines (Q-1-13) and opens a medicine at its code; this editor is
 * keyed on that code. Creating adds a medicine with its first code; editing corrects the
 * code and the position it hangs off.
 *
 * The identity fields are the POSITION's and are shared with every sibling code — editing
 * them moves all of them, which is why `siblings` puts a notice above them when there are
 * any. Everything else on this form is this registration's alone, and a save writes only
 * what changed (`medicine-form-payload.ts`).
 */
function emptyForm(): FormState {
  return {
    dci: '',
    pharmaceuticalForm: '',
    dose: '',
    volume: '',
    division: '',
    atcCode: '',
    code: '',
    kind: 'AUTHORIZED',
    barcode: '',
    commercialName: '',
    manufacturer: '',
    manufacturerCountry: '',
    authorizationHolder: '',
    authorizationHolderCountry: '',
    authorizationExpiresOn: '',
    active: true,
  };
}

function toFormState(row: MedicineCodeRow): FormState {
  return {
    dci: row.dci,
    pharmaceuticalForm: row.pharmaceuticalForm,
    dose: row.dose,
    volume: row.volume,
    division: row.division,
    atcCode: row.atcCode,
    code: row.code,
    kind: row.kind,
    barcode: row.barcode,
    commercialName: row.commercialName,
    manufacturer: row.manufacturer,
    manufacturerCountry: row.manufacturerCountry,
    authorizationHolder: row.authorizationHolder,
    authorizationHolderCountry: row.authorizationHolderCountry,
    authorizationExpiresOn: row.authorizationExpiresOn,
    active: row.active,
  };
}

/**
 * The medicine that already holds the code a creation was refused for, when the refusal names
 * it (`409 MEDICINE_CODE_TAKEN`, UC20-08, TC-UC20-08-02). The static sentence of the banner says
 * the code exists; this is the part that tells the administrator where.
 */
export function medicineHoldingCode(err: unknown): string | null {
  if (!isNormalizedApiError(err) || err.code !== 'MEDICINE_CODE_TAKEN') return null;
  return err.existingMedicineLabel ?? null;
}

export function MedicineFormPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { code: codeParam } = useParams<{ code: string }>();
  const isEdit = Boolean(codeParam);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [initialForm, setInitialForm] = useState<FormState>(emptyForm);
  const [submitError, setSubmitError] = useState<ApiRefusal | null>(null);
  const [submitted, setSubmitted] = useState(false);
  // The position that holds the code the last creation was refused for.
  const [codeHeldBy, setCodeHeldBy] = useState<string | null>(null);

  // Keyed on the code, because that is what this editor edits. The position it belongs
  // to comes back on the same row, which is how the identity fields and `medicineId`
  // reach the form without a second read.
  const detailQuery = useQuery({
    queryKey: ['admin-medicine-code', codeParam],
    queryFn: () => fetchMedicineCode(codeParam ?? ''),
    enabled: isEdit,
  });

  useEffect(() => {
    if (detailQuery.data) {
      const next = toFormState(detailQuery.data);
      setForm(next);
      setInitialForm(next);
    }
  }, [detailQuery.data]);

  const medicineId = detailQuery.data?.medicineId ?? '';

  // How many other registrations share this position. It is what makes the identity
  // fields dangerous to edit: they are the position's, and moving them moves every
  // sibling. Read from the register rather than a dedicated endpoint — the same filter
  // the list uses, asked for one page.
  const siblingsQuery = useQuery({
    queryKey: ['admin-medicine-siblings', medicineId, form.dci, form.dose],
    queryFn: () =>
      fetchMedicineCodePage({
        q: form.dci,
        kind: '',
        status: 'all',
        expiry: 'all',
        atcGroup: '',
        source: '',
        limit: MEDICINES_PAGE_SIZE,
        offset: 0,
      }),
    enabled: isEdit && medicineId.length > 0 && form.dci.length > 0,
  });
  const siblings = (siblingsQuery.data?.codes ?? []).filter(
    (row) => row.medicineId === medicineId && row.code !== form.code,
  );

  const create = useMutation({
    mutationFn: () => createMedicine(createPayload(form)),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['admin-medicines'] });
      queryClient.invalidateQueries({ queryKey: ['admin-medicine-codes'] });
      toast({
        variant: 'success',
        title: t('medicines.toast.created'),
        text: medicineLabel(created),
      });
      // Back to the register, on the new position (TC-UC20-08-01): the search is the
      // code just typed, which is unique, so the position is on the first page whatever
      // the register's order, and `highlight` marks it.
      navigate(
        `/system-admin/medicines?${new URLSearchParams({
          q: form.code.trim(),
          highlight: created.id,
        }).toString()}`,
      );
    },
    // Per-operation fallback: a failed creation must not report a failed save.
    // And a creation whose answer never arrived must not report a failure at
    // all — the write may have gone through, so the headline says what we know
    // (nothing) rather than what would be convenient to say.
    onError: (err) => {
      setCodeHeldBy(medicineHoldingCode(err));
      setSubmitError(
        apiRefusal(
          err,
          isRequestUnresolved(err)
            ? 'medicines.error.create_unresolved'
            : 'medicines.error.create_failed',
        ),
      );
    },
  });

  /**
   * One save, up to three writes, in order, each only for what changed.
   *
   * The position first, when its identity moved: the identity fields and the ATC live on
   * `catalog.medicine`, and a code cannot be written against a position that does not yet
   * carry the values the administrator just typed. That write is the identity and nothing
   * else — never the brand, producer, country or date, which the form shows from the code,
   * and never `active`, which is the position's switch and not this form's (BUG-1-082).
   * Then the code, when one of its own fields moved. Then the activation, only when it
   * moved — `SetCodeActive` writes an audit row saying a person flipped it, and a save
   * that did not touch the switch must not claim one.
   *
   * Sequential and not parallel because the second write depends on the first having
   * landed, and because a half-applied save is easier to reason about when the order is
   * fixed than when it is a race.
   */
  const update = useMutation({
    mutationFn: async () => {
      const position = positionIdentityPatch(form, initialForm);
      if (position) await updateMedicine(medicineId, position);
      const codeWrite = codePatch(form, initialForm);
      if (codeWrite) await upsertMedicineCode(medicineId, codeWrite);
      if (form.active !== initialForm.active) {
        await setMedicineCodeActive(medicineId, form.code, form.active);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-medicines'] });
      queryClient.invalidateQueries({ queryKey: ['admin-medicine-codes'] });
      queryClient.invalidateQueries({ queryKey: ['admin-medicine-code', form.code] });
      toast({
        variant: 'success',
        title: t('medicines.toast.saved'),
        text: form.code,
      });
      navigate(`/system-admin/medicines/code/${form.code}`);
    },
    onError: (err) =>
      setSubmitError(
        apiRefusal(
          err,
          isRequestUnresolved(err)
            ? 'medicines.error.save_unresolved'
            : 'medicines.error.save_failed',
        ),
      ),
  });

  const codeMissing = form.code.trim().length === 0;
  const isDirty = JSON.stringify(form) !== JSON.stringify(initialForm);
  const missing = missingFields(form, isEdit, initialForm);
  // Which labels carry the asterisk: the same list the save checks.
  const required = requiredFields(isEdit, initialForm);
  // A refused read is not evidence the medicine is gone: this branch answered
  // every failure with "the record does not exist", so a 503 from a database
  // that could not hand out a connection sent the administrator looking for a
  // medicine that was sitting where they left it.
  const loadError = isEdit && detailQuery.isError ? loadFailureKind(detailQuery.error) : null;

  function fieldError(field: RequiredField): string | undefined {
    return submitted && missing.includes(field) ? t('form.field.required') : undefined;
  }

  function handleSave(): void {
    setSubmitError(null);
    setCodeHeldBy(null);
    setSubmitted(true);
    if (missing.length > 0 || codeMissing) return;
    if (isEdit) update.mutate();
    else create.mutate();
  }

  return (
    <FormPage
      breadcrumb={[
        { label: t('nav.medicines'), href: '/system-admin/medicines' },
        { label: isEdit ? t('medicines.edit.title') : t('medicines.new.title') },
      ]}
      title={isEdit ? t('medicines.edit.title') : t('medicines.new.title')}
      lede={isEdit ? t('medicines.edit.lede') : t('medicines.new.lede')}
      saveLabel={isEdit ? t('medicines.action.save') : t('medicines.action.save_new')}
      isSaving={create.isPending || update.isPending}
      isDirty={isDirty}
      isLoading={detailQuery.isLoading}
      loadError={loadError}
      refusal={submitError}
      onSave={handleSave}
      onCancel={() =>
        navigate(isEdit ? `/system-admin/medicines/code/${form.code}` : '/system-admin/medicines')
      }
    >
      {/* First, and in both modes. A registration is opened with two questions — does it
          still count, and is it authorised — and the record page answers them in the
          same order. `Statut` is the CODE's, not the position's: the register draws it
          per row, and a position can be live while one of its registrations has gone
          stale. */}
      <FormSection title={t('medicines.section.state')} lede={t('medicines.section.state_lede')}>
        {/* Statut then Tip autorizație, the order the record page states them in, and
            side by side because they are one question asked twice. The switch sits in a
            FormField so it carries a visible label: `label` on mud-switch is only the
            accessible name — it lands on the native input's `aria-label` and renders
            nothing — and the visible text goes through the `label` SLOT. */}
        <FormRow columns={2}>
          <FormField label={t('medicines.column.status')}>
            <MudSwitch
              checked={form.active}
              label={
                form.active
                  ? t('medicines.field.status_active')
                  : t('medicines.field.status_inactive')
              }
              onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                setForm({ ...form, active: e.detail.checked })
              }
            >
              <span slot="label">
                {form.active
                  ? t('medicines.field.status_active')
                  : t('medicines.field.status_inactive')}
              </span>
            </MudSwitch>
          </FormField>
          <FormField label={t('medicines.field.authorization_type')} required>
            <MudSelect
              size="medium"
              value={form.kind}
              aria-label={t('medicines.field.authorization_type')}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, kind: e.detail.value as MedicineCodeKind })
              }
            >
              {MEDICINE_CODE_KINDS.filter((kind) => kind !== 'BARCODE').map((kind) => (
                <option key={kind} value={kind}>
                  {t(`medicines.kind.${kind}`)}
                </option>
              ))}
            </MudSelect>
          </FormField>
        </FormRow>
      </FormSection>

      {/* One code, because a row is a code. What stood here listed every sibling of the
          position and carried a drawer to add more — the shape the nomenclator had
          before the register was re-grained. Adding a further registration is now
          "Adaugă medicament" with the same identity fields, which is what it always
          was: a new code IS a new row. */}
      <FormSection
        title={
          <>
            {t('medicines.section.codes')}
            <SectionTip text={t('medicines.section.codes_tip')} />
          </>
        }
        lede={t('medicines.section.codes_lede')}
      >
        <FormRow columns={2}>
          <FormField
            label={t('medicines.column.code')}
            required
            hint={
              isEdit
                ? t('medicines.codes.field.code_immutable')
                : t('medicines.codes.field.code_hint')
            }
            error={
              submitted && codeMissing
                ? t('form.field.required')
                : codeHeldBy
                  ? t('medicines.error.code_taken_on', {
                      code: form.code.trim(),
                      medicine: codeHeldBy,
                    })
                  : undefined
            }
          >
            {/* Read-only once written. The code is the row's primary key and the key
                every stock movement in the system points at (PRD: Stock → MedicineCode);
                retyping it here would silently move those movements to another product. */}
            <MudTextInput
              size="md"
              value={form.code}
              readonly={isEdit}
              placeholder={t('medicines.codes.placeholder.code')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, code: e.detail.value })
              }
            />
          </FormField>
          <FormField label={t('medicines.field.barcode')} hint={t('medicines.field.barcode_hint')}>
            <MudTextInput
              size="md"
              value={form.barcode}
              placeholder={t('medicines.placeholder.barcode')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, barcode: e.detail.value })
              }
            />
          </FormField>
        </FormRow>
      </FormSection>

      {/* The five identity fields belong to the POSITION and are shared with every other
          registration of it. Editing them here moves all of them, which is a thing an
          administrator has to be told before they type, not after they save. */}
      {siblings.length > 0 ? (
        <MudInfoBox emphasis="strong" variant="warning">
          {t('medicines.identity.shared_warning', { count: siblings.length })}
        </MudInfoBox>
      ) : null}

      <FormSection
        title={
          <>
            {t('medicines.section.identity')}
            <SectionTip text={t('medicines.section.identity_tip')} />
          </>
        }
        lede={t('medicines.section.identity_lede')}
      >
        {/* DCI and the pharmaceutical form share a row of two, not three: their 95th
            percentiles are 45 and 43 characters against the 24 a third of a row showed,
            and they are two of the five fields that DEFINE the unique medicine. The dose,
            the volume and the division are short enough to sit together below. */}
        <FormRow columns={2}>
          <FormField label={t('medicines.field.dci')} required error={fieldError('dci')}>
            <MudTextInput
              size="md"
              value={form.dci}
              placeholder={t('medicines.placeholder.dci')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, dci: e.detail.value })
              }
            />
          </FormField>
          <FormField
            label={t('medicines.field.form')}
            required
            error={fieldError('pharmaceuticalForm')}
          >
            <MudTextInput
              size="md"
              value={form.pharmaceuticalForm}
              placeholder={t('medicines.placeholder.form')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, pharmaceuticalForm: e.detail.value })
              }
            />
          </FormField>
        </FormRow>
        <FormRow columns={3}>
          <FormField label={t('medicines.field.dose')} required error={fieldError('dose')}>
            <MudTextInput
              size="md"
              value={form.dose}
              placeholder={t('medicines.placeholder.dose')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, dose: e.detail.value })
              }
            />
          </FormField>
          <FormField label={t('medicines.field.volume')}>
            <MudTextInput
              size="md"
              value={form.volume}
              placeholder={t('medicines.placeholder.volume')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, volume: e.detail.value })
              }
            />
          </FormField>
          <FormField
            label={t('medicines.field.division')}
            required={required.includes('division')}
            error={fieldError('division')}
          >
            <MudTextInput
              size="md"
              value={form.division}
              placeholder={t('medicines.placeholder.division')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, division: e.detail.value })
              }
            />
          </FormField>
        </FormRow>
      </FormSection>

      <FormSection
        title={t('medicines.section.attributes')}
        lede={t('medicines.section.attributes_lede')}
      >
        <FormRow columns={2}>
          <FormField
            label={t('medicines.field.commercial_name')}
            required={required.includes('commercialName')}
            error={fieldError('commercialName')}
          >
            <MudTextInput
              size="md"
              value={form.commercialName}
              placeholder={t('medicines.placeholder.commercial_name')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, commercialName: e.detail.value })
              }
            />
          </FormField>
          <FormField label={t('medicines.field.atc')}>
            <MudTextInput
              size="md"
              value={form.atcCode}
              placeholder={t('medicines.placeholder.atc')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, atcCode: e.detail.value })
              }
            />
          </FormField>
        </FormRow>
        {/* The producer takes the whole row. It is the longest value on the form by a
            wide margin — measured over the 6289 live codes its MEDIAN is 35 characters
            and its 95th percentile 82, against the 39 a half-row box showed, so half the
            register was already at the edge and one in twenty saw less than half its
            value. The column is varchar(500) for the reason the width now reflects: both
            feeds serve the producer as a `;`-separated list of every production site,
            and the longest is 427 characters. */}
        <FormRow>
          <FormField
            label={t('medicines.field.manufacturer')}
            fullWidth
            required={required.includes('manufacturer')}
            error={fieldError('manufacturer')}
          >
            <MudTextInput
              size="md"
              value={form.manufacturer}
              placeholder={t('medicines.placeholder.manufacturer')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, manufacturer: e.detail.value })
              }
            />
          </FormField>
        </FormRow>
        {/* The certificate holder. Code-only — `catalog.medicine` carries no holder, so
            unlike the brand and the producer there is nothing above this row to fall back
            to. Both feeds serve it on 6279 of 6289 records, and it is a different
            organisation from the producer on about a third of the register, which is why
            it is its own pair of fields and not a rewording of the two above. */}
        {/* On its own row and deliberately still two columns wide: a date picker
            stretched across the whole field column would claim it takes a long
            value. The wireframe puts it in a `.grid-2` for the same reason. */}
        <FormRow columns={2}>
          <FormField label={t('medicines.field.holder')}>
            <MudTextInput
              size="md"
              value={form.authorizationHolder}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, authorizationHolder: e.detail.value })
              }
            />
          </FormField>
          <FormField
            label={t('medicines.field.authorization_expiry')}
            hint={t('medicines.field.authorization_expiry_hint')}
          >
            {/* Day-month-year, which is how Moldova reads a date, how the
                wireframe writes the field (`ZZ/LL/AAAA`) and what AGE itself
                defaults to. The `format="YYYY-MM-DD"` this replaced was not a
                decision about entry order: with an ISO mask the displayed
                string and the wire value are identical and no conversion has
                to be written. `isoToDateInput` is that conversion. */}
            <DraftDateInput
              size="md"
              placeholder={t('common.date.placeholder')}
              aria-label={t('medicines.field.authorization_expiry')}
              value={isoToDateInput(form.authorizationExpiresOn)}
              // AGE-BUG(date-picker-mudChange-escapes-shadow-root) ·
              // vendor/age-design/SOURCE_COMMIT 7322a157 — picking a day fires
              // `mudChange` twice here; the second is the calendar's own event,
              // it carries no `isoValue`, and unguarded it cleared the field
              // the first one had just filled.
              onMudChange={onDateCommit((d) =>
                setForm({ ...form, authorizationExpiresOn: d.isoValue ?? '' }),
              )}
            />
          </FormField>
        </FormRow>
        {/* The two countries side by side: they are the same question asked of two
            different organisations, and they differ on about a third of the register —
            1212 of 6274 records measured on the authorised feed. Reading them apart
            hides exactly the case that matters. */}
        <FormRow columns={2}>
          {/* Not required: no AMDM feed serves a producer country and it is empty on
              all 6289 codes, so demanding it would make every synced position unsavable.
              What the feeds DO serve is the certificate holder's country, which is its
              own field below. */}
          <FormField label={t('medicines.field.country')}>
            <MudTextInput
              size="md"
              value={form.manufacturerCountry}
              placeholder={t('medicines.placeholder.country')}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, manufacturerCountry: e.detail.value })
              }
            />
          </FormField>
          <FormField label={t('medicines.field.holder_country')}>
            <MudTextInput
              size="md"
              value={form.authorizationHolderCountry}
              onMudInput={(e: CustomEvent<{ value: string }>) =>
                setForm({ ...form, authorizationHolderCountry: e.detail.value })
              }
            />
          </FormField>
        </FormRow>
      </FormSection>
    </FormPage>
  );
}

/**
 * The explanation a section heading carries, as `.config-sect h4 .info-tip` in
 * the wireframe: the rule behind the group of fields, which is too long to be a
 * lede and has nowhere else on the page to go. The icon-in-a-tooltip is the
 * shape AggregateFormPage already uses for the same job.
 */
function SectionTip({ text }: { text: string }): JSX.Element {
  return (
    <MudTooltip content={text} position="top">
      {/* `slot="trigger"` is not optional: mud-tooltip's shadow root has a
          named trigger slot and a default slot for the bubble, so an unslotted
          icon is placed INSIDE the bubble. The trigger then measures 0x0, and
          the whole tip is invisible and unhoverable — which is what the two
          older copies of this pattern have been doing. */}
      <MudIcon slot="trigger" name="circle-info" size={16} />
    </MudTooltip>
  );
}
